/**
 * Settings → System: the language-model provider (ADR-004 §7.1). Everything here needs `settings.system`
 * (system.admin) — the founder only (operator decision 10); everyone else gets 403. No route returns a key.
 */
import { IdempotencyHeaders, IfMatchHeaders } from './common.js';
import { ClaudeLoginStart, ClaudeLoginStatus, CompleteClaudeLoginBody, LlmSecretParams, LlmProviderStatus, LlmSettingsResponse, LlmUsageQuery, LlmUsageResponse, PatchLlmSettingsBody, PutLlmSecretBody, TestLlmBody, } from './llm.js';
import { API_BASE, defineRoute } from './route.js';
const SYSTEM = ['settings.system'];
const NOTE = 'System admin (the founder). Others: 403.';
const CONFIG_ETAG = { ETag: 'W/"<config_version>"' };
export const systemRoutes = {
    'llmSettings.get': defineRoute({
        id: 'llmSettings.get',
        method: 'GET',
        path: `${API_BASE}/settings/llm`,
        summary: 'The AI provider settings: providers (key last4, test result, ready), the per-purpose matrix, models, prices',
        description: 'Never returns a key or ciphertext. Poll it after POST /settings/llm/test until no check is `running`.',
        access: { auth: 'session', anyOf: SYSTEM, note: NOTE },
        success: { status: 200, description: 'The settings', schema: LlmSettingsResponse, headers: CONFIG_ETAG },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'llmSettings.update': defineRoute({
        id: 'llmSettings.update',
        method: 'PATCH',
        path: `${API_BASE}/settings/llm`,
        summary: 'Change the provider or model per purpose, the model allow-list, prices, the daily spend ceiling',
        description: 'If-Match is the config_version (shared with the other Settings groups). A purpose or the default may newly ' +
            'name only a ready provider (configured and tested since its key was set) — 422 saying what is missing — and a ' +
            'model in that provider’s known_models. Applies from the next job, never one in flight. Audited.',
        access: { auth: 'session', anyOf: SYSTEM, note: NOTE },
        headers: IfMatchHeaders,
        body: PatchLlmSettingsBody,
        success: { status: 200, description: 'The settings after the change', schema: LlmSettingsResponse, headers: CONFIG_ETAG },
        errors: ['no_session', 'forbidden', 'validation_failed', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'llmSettings.putSecret': defineRoute({
        id: 'llmSettings.putSecret',
        method: 'PUT',
        path: `${API_BASE}/settings/llm/secrets/{provider}`,
        summary: 'Set or replace a provider’s API key (write-only)',
        description: 'Encrypted at rest (AES-256-GCM under LLM_SECRET_KEK) and never returned: the answer carries only last4 and ' +
            'set_at. The provider becomes untested until its next green test. Audited as "who set which provider’s key" ' +
            'with last4 only — the key never reaches the audit trail or the logs. 422 when the server has no LLM_SECRET_KEK.',
        // The provider is one of three fixed names, so nothing is revealed by saying 403 rather than 404.
        access: { auth: 'session', anyOf: SYSTEM, deny: 'forbidden', note: NOTE },
        params: LlmSecretParams,
        body: PutLlmSecretBody,
        success: { status: 200, description: 'The provider, with the new key’s last4', schema: LlmProviderStatus },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'llmSettings.deleteSecret': defineRoute({
        id: 'llmSettings.deleteSecret',
        method: 'DELETE',
        path: `${API_BASE}/settings/llm/secrets/{provider}`,
        summary: 'Remove a provider’s API key',
        description: '409 conflict while a purpose or the default still names that provider (move them first). Removing a key that is not there changes nothing (200). Audited.',
        // The provider is one of three fixed names, so nothing is revealed by saying 403 rather than 404.
        access: { auth: 'session', anyOf: SYSTEM, deny: 'forbidden', note: NOTE },
        params: LlmSecretParams,
        success: { status: 200, description: 'The provider, without a key', schema: LlmProviderStatus },
        errors: ['no_session', 'forbidden', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'llmSettings.test': defineRoute({
        id: 'llmSettings.test',
        method: 'POST',
        path: `${API_BASE}/settings/llm/test`,
        summary: 'Run one tiny real call per configured provider (or the one named) — in the worker',
        description: 'Answers 202 with each tested provider’s check `running`; the worker records latency, the model that answered ' +
            'and whether the JSON validated, and GET /settings/llm shows it. A provider already being tested is not tested ' +
            'twice. 422 for a keyed provider with no key, or a model that is not allowed. Each call is recorded in usage ' +
            '(purpose `test`).',
        access: { auth: 'session', anyOf: SYSTEM, note: NOTE },
        body: TestLlmBody,
        success: { status: 202, description: 'Tests queued', schema: LlmSettingsResponse },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'none',
        rateLimit: 'write',
    }),
    'llmSettings.usage': defineRoute({
        id: 'llmSettings.usage',
        method: 'GET',
        path: `${API_BASE}/settings/llm/usage`,
        summary: 'Spend and calls by provider, model and purpose; the CLI labelled "not metered"',
        access: { auth: 'session', anyOf: SYSTEM, note: NOTE },
        query: LlmUsageQuery,
        success: { status: 200, description: 'Usage for the period', schema: LlmUsageResponse },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'claudeLogin.get': defineRoute({
        id: 'claudeLogin.get',
        method: 'GET',
        path: `${API_BASE}/settings/llm/claude-login`,
        summary: 'Whether the Claude CLI is signed in here: state, who signed in and when, when it expires, last4',
        description: 'Never the token: only its last four characters. `pending` while a sign-in is open (its link is in `pending`, ' +
            'so a reload can carry on); `expired` once past `expires_at`; `error` when the stored sign-in cannot be used ' +
            '(the last Claude CLI test after sign-in failed, or it was stored under an encryption key this server no ' +
            'longer holds) — sign in again.',
        access: { auth: 'session', anyOf: SYSTEM, note: NOTE },
        success: { status: 200, description: 'The sign-in status', schema: ClaudeLoginStatus },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'claudeLogin.start': defineRoute({
        id: 'claudeLogin.start',
        method: 'POST',
        path: `${API_BASE}/settings/llm/claude-login/start`,
        summary: 'Start signing in to Claude: the claude.ai address to open',
        description: 'The same sign-in `claude setup-token` does (Claude OAuth with PKCE, scope user:inference, a token good for a ' +
            'year), run by the server. Open `auth_url`, sign in with the Claude account whose subscription the CLI should ' +
            'use, then paste the code claude.ai shows into POST …/complete within 10 minutes. Starting again replaces an ' +
            'open sign-in. 422 when the server has no LLM_SECRET_KEK (the token could not be stored). Like every creating ' +
            'POST it takes an Idempotency-Key: a retried press answers the same sign-in instead of opening another.',
        headers: IdempotencyHeaders,
        access: { auth: 'session', anyOf: SYSTEM, note: NOTE },
        success: { status: 201, description: 'The sign-in to open', schema: ClaudeLoginStart },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'claudeLogin.complete': defineRoute({
        id: 'claudeLogin.complete',
        method: 'POST',
        path: `${API_BASE}/settings/llm/claude-login/complete`,
        summary: 'Finish signing in with the code claude.ai showed; stores the token (write-only)',
        description: 'Exchanges the code with Claude and stores the token encrypted (AES-256-GCM under LLM_SECRET_KEK); the worker ' +
            'then runs the CLI with it, ahead of any CLAUDE_CODE_OAUTH_TOKEN in its environment. Replaces an earlier ' +
            'sign-in. The Claude CLI becomes untested until its next green POST /settings/llm/test. 422 with a plain reason ' +
            'when Claude refuses the code (mistyped, used, or from another sign-in) or the sign-in is no longer open; 503 ' +
            'when Claude cannot be reached. Audited as who signed in, with last4 only — the token never reaches a response, ' +
            'a log line or the audit trail.',
        access: { auth: 'session', anyOf: SYSTEM, note: NOTE },
        body: CompleteClaudeLoginBody,
        success: { status: 200, description: 'Signed in', schema: ClaudeLoginStatus },
        errors: ['no_session', 'forbidden', 'validation_failed', 'upstream_unavailable'],
        idempotency: 'none',
        rateLimit: 'write',
    }),
    'claudeLogin.delete': defineRoute({
        id: 'claudeLogin.delete',
        method: 'DELETE',
        path: `${API_BASE}/settings/llm/claude-login`,
        summary: 'Sign the Claude CLI out here: forget the stored token and any open sign-in',
        description: 'The worker then falls back to CLAUDE_CODE_OAUTH_TOKEN from its environment, if one is set; without it Claude ' +
            'CLI calls fail until someone signs in again (test it after signing out). The token is removed from this server ' +
            'only — Claude itself is not told. Signing out when not signed in changes nothing (200). Audited.',
        access: { auth: 'session', anyOf: SYSTEM, note: NOTE },
        success: { status: 200, description: 'Signed out', schema: ClaudeLoginStatus },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
};
//# sourceMappingURL=routes.system.js.map